/**
 * API-backed Soil Intelligence command center.
 *
 * The dashboard aggregates only fields supplied by listProjects. Cross-project
 * report totals and soil-test warnings are intentionally not synthesized.
 */
import { keyframes } from "@emotion/react";
import {
	Box,
	Button,
	Chip,
	Stack,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import RefreshIcon from "@mui/icons-material/Refresh";
import type {
	OrgUsageSummaryDTO,
	ProjectSummaryDTO,
} from "@flaha/shared-types";
import { ProjectStatus, SubscriptionTier } from "@flaha/shared-types";
import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { useAuth } from "../auth";
import { AlertsPanel } from "../features/dashboard/components/AlertsPanel";
import { DashboardMetricCards } from "../features/dashboard/components/DashboardMetricCards";
import { QuickActionsPanel } from "../features/dashboard/components/QuickActionsPanel";
import { RecentProjectsPanel } from "../features/dashboard/components/RecentProjectsPanel";
import { SystemStatusPanel } from "../features/dashboard/components/SystemStatusPanel";
import { GlassSurface } from "../layouts/components/GlassSurface";
import { usePageHeader } from "../layouts/PageHeaderContext";
import { getApiClient, getApiClientMode } from "../services/apiClientProvider";
import { reducedMotionMediaQuery } from "../theme/motionTokens";

const MAX_RECENT_PROJECTS = 5;

const dashboardReveal = keyframes`
	from { opacity: 0; transform: translate3d(0, 10px, 0); }
	to { opacity: 1; transform: translate3d(0, 0, 0); }
`;

export function DashboardPage() {
	const { status: sessionStatus, user, activeOrganization } = useAuth();
	const apiMode = getApiClientMode();
	const theme = useTheme();
	const isWide = useMediaQuery(theme.breakpoints.up("md"));
	const prefersReducedMotion = useMediaQuery(
		"(prefers-reduced-motion: reduce)",
		{ noSsr: true }
	);
	const [projects, setProjects] = useState<ProjectSummaryDTO[] | null>(null);
	const [usage, setUsage] = useState<OrgUsageSummaryDTO | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [retryKey, setRetryKey] = useState(0);

	const organizationLabel = activeOrganization?.name ?? "No active organization";

	usePageHeader({
		title: "Soil Intelligence Workspace",
		subtitle: `${organizationLabel}${user ? ` · ${user.displayName}` : ""}`,
		breadcrumbs: [{ label: "Home", to: "/" }, { label: "Dashboard" }],
	});

	useEffect(() => {
		if (sessionStatus !== "authenticated") return;
		let cancelled = false;
		setProjects(null);
		setError(null);

		void getApiClient()
			.listProjects({})
			.then((response) => {
				if (!cancelled) setProjects(response.projects);
			})
			.catch((cause: unknown) => {
				if (!cancelled) {
					setError(cause instanceof Error ? cause.message : String(cause));
				}
			});

		void getApiClient()
			.getMyUsage()
			.then((res) => {
				if (!cancelled) setUsage(res.usage);
			})
			.catch(() => {
				if (!cancelled) setUsage(null);
			});

		return () => {
			cancelled = true;
		};
	}, [sessionStatus, activeOrganization?.id, retryKey]);

	const metricValues = {
		projects: projects?.length ?? null,
		samples:
			projects?.reduce((sum, project) => sum + project.sampleCount, 0) ?? null,
		activeProjects:
			projects?.filter((project) => project.status === ProjectStatus.ACTIVE).length ?? null,
		archivedProjects:
			projects?.filter((project) => project.status === ProjectStatus.ARCHIVED).length ?? null,
	};
	const loading = projects === null && error === null;

	return (
		<Box
			data-testid="dashboard-surface"
			data-layout={isWide ? "wide" : "compact"}
			data-motion={prefersReducedMotion ? "reduced" : "full"}
			sx={{
				overflow: "hidden",
				m: { xs: -2, sm: -3, lg: -4 },
				p: { xs: 2, sm: 3, lg: 4 },
				color: "workspace.textPrimary",
				background:
					"radial-gradient(circle at 94% 4%, rgba(74,156,197,0.1), transparent 28%), radial-gradient(circle at 8% 36%, rgba(119,169,87,0.1), transparent 30%), linear-gradient(155deg, #0C100E, #15110F 60%, #101612)",
			}}
		>
			<Stack
				spacing={3}
				sx={{
					animation: `${dashboardReveal} ${theme.motion.duration.entry}ms ${theme.motion.easing.enter} both`,
					[reducedMotionMediaQuery]: { animation: "none", transform: "none" },
				}}
			>
				<GlassSurface
					component="section"
					aria-labelledby="dashboard-command-title"
					intensity="strong"
					depth="floating"
					sx={{
						p: { xs: 2.5, md: 3.5 },
						backgroundImage:
							"linear-gradient(120deg, rgba(119,169,87,0.12), transparent 52%), linear-gradient(300deg, rgba(74,156,197,0.08), transparent 48%)",
					}}
				>
					<Stack
						direction={{ xs: "column", md: "row" }}
						justifyContent="space-between"
						alignItems={{ xs: "flex-start", md: "center" }}
						gap={3}
					>
						<Stack spacing={1.25} sx={{ minWidth: 0 }}>
							<Typography
								variant="overline"
								sx={{ color: "workspace.green", letterSpacing: "0.12em", fontWeight: 700 }}
							>
								Active soil-intelligence context
							</Typography>
							<Typography id="dashboard-command-title" component="h1" variant="h3">
								Soil Intelligence Command Center
							</Typography>
							<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
								<BusinessIcon aria-hidden="true" sx={{ color: "workspace.blue" }} />
								<Typography variant="body1" sx={{ color: "workspace.textSecondary" }}>
									Organization: <strong>{organizationLabel}</strong>
								</Typography>
								<Chip label={apiMode === "real" ? "Live API" : "Demonstration client"} size="small" />
								{usage && (
									<>
										<Chip
											label={`Plan: ${usage.entitlements.tier}${
												usage.entitlements.grandfathered ? " (demo)" : ""
											}`}
											size="small"
											color={
												usage.entitlements.tier === SubscriptionTier.FREE
													? "default"
													: "success"
											}
											variant="outlined"
										/>
										{usage.entitlements.maxLifetimeTests != null && (
											<Chip
												label={`Tests: ${usage.lifetimeTestsUsed} of ${usage.entitlements.maxLifetimeTests} lifetime`}
												size="small"
												variant="outlined"
											/>
										)}
										{usage.entitlements.maxTestsPerBillingPeriod != null && (
											<Chip
												label={`Tests: ${usage.periodTestsUsed} of ${usage.entitlements.maxTestsPerBillingPeriod} this period`}
												size="small"
												variant="outlined"
											/>
										)}
									</>
								)}
							</Stack>
							<Typography variant="body2" sx={{ color: "workspace.textMuted", maxWidth: 760 }}>
								Project and sample totals below come from the active organization’s project
								request. The current dashboard contract does not expose a cross-project
								report total; use the report library for report activity.
							</Typography>
						</Stack>
						<Stack direction="row" flexWrap="wrap" gap={1.25}>
							{error && (
								<Button
									variant="outlined"
									startIcon={<RefreshIcon />}
									onClick={() => setRetryKey((key) => key + 1)}
									sx={{ color: "workspace.textPrimary", borderColor: "workspace.borderStrong" }}
								>
									Retry project data
								</Button>
							)}
							<Button component={RouterLink} to="/reports" variant="contained">
								Open report library
							</Button>
						</Stack>
					</Stack>
				</GlassSurface>

				<Box component="section" aria-labelledby="dashboard-metrics-title">
					<Typography id="dashboard-metrics-title" component="h2" variant="h5" sx={{ mb: 1.5 }}>
						Organization overview
					</Typography>
					<DashboardMetricCards
						values={metricValues}
						loading={loading}
						unavailable={error !== null}
					/>
				</Box>

				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: {
							xs: "minmax(0, 1fr)",
							xl: "minmax(0, 1.65fr) minmax(320px, 0.85fr)",
						},
						gap: 3,
						alignItems: "start",
					}}
				>
					<RecentProjectsPanel
						projects={projects}
						error={error}
						limit={MAX_RECENT_PROJECTS}
						onRetry={() => setRetryKey((key) => key + 1)}
					/>
					<Stack spacing={3} sx={{ minWidth: 0 }}>
						<AlertsPanel projects={projects} error={error} />
						<SystemStatusPanel apiMode={apiMode} />
					</Stack>
				</Box>

				<QuickActionsPanel />
			</Stack>
		</Box>
	);
}
